import React, { useState, useEffect, useRef, useMemo } from "react";
import { getPriceChangeLogs } from "./service";
import { Spinner, Modal, ModalHeader, ModalBody, Collapse } from "reactstrap";

const formatRelativeTime = (dateString) => {
  if (!dateString) return "–";
  try {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSeconds < 60) return "Just now";
    if (diffMinutes < 60)
      return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  } catch (e) {
    return "–";
  }
};

// Accordion Item Component with collapsible functionality
const AccordionItem = ({
  title,
  badge,
  priceDisplay,
  isPositive,
  isOpen,
  onToggle,
  children,
  level = 0,
}) => {
  const marginClass =
    level === 0 ? "" : level === 1 ? "ms-3" : level === 2 ? "ms-4" : "ms-5";
  const fontClass =
    level === 0 ? "fw-bold" : level === 1 ? "fw-semibold" : "small";

  return (
    <div className={`${marginClass} mb-2`}>
      <div
        className={`d-flex justify-content-between align-items-center pointer py-1 ${children ? "hover-bg-light rounded px-2" : ""}`}
        onClick={children ? onToggle : undefined}
        style={{ cursor: children ? "pointer" : "default" }}
      >
        <div className="d-flex align-items-center gap-2">
          {children && (
            <i
              className={`fa-solid fa-chevron-${isOpen ? "down" : "right"} text-muted`}
              style={{ fontSize: "0.7rem", width: "12px" }}
            ></i>
          )}
          {!children && <span style={{ width: "12px" }}></span>}
          <span className={fontClass}>{title}</span>
          {badge && (
            <span
              className={`badge ${badge.type === "added" ? "bg-success-subtle text-success" : badge.type === "removed" ? "bg-danger-subtle text-danger" : "bg-secondary-subtle text-secondary"}`}
              style={{ fontSize: "0.65rem" }}
            >
              {badge.text}
            </span>
          )}
        </div>
        <span
          className={`${fontClass} ${isPositive ? "text-success" : "text-danger"}`}
        >
          {priceDisplay}
        </span>
      </div>
      {children && (
        <Collapse isOpen={isOpen}>
          <div className="mt-1">{children}</div>
        </Collapse>
      )}
    </div>
  );
};

// Modal-based Detailed View with Accordions
const PriceLogDetailModal = ({ isOpen, toggle, log, formatCurrency }) => {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  // Process order items into hierarchical structure
  const { roomsData, ungroupedItems, allAccordionIds } = useMemo(() => {
    const rooms = {};
    const ungrouped = [];
    const ids = new Set();

    if (!log?.order_items)
      return {
        roomsData: rooms,
        ungroupedItems: ungrouped,
        allAccordionIds: ids,
      };

    log.order_items.forEach((item, index) => {
      const roomName = item.room_name || null;
      const groupName =
        item.group_name || item.section_name || item.group_id || null;
      const itemWithIndex = { ...item, originalIndex: index };

      // Generate IDs for accordion tracking
      if (roomName) {
        const roomId = `room-${roomName}`;
        ids.add(roomId);

        if (!rooms[roomName]) {
          rooms[roomName] = { groups: {}, ungrouped: [] };
        }

        if (groupName) {
          const groupId = `group-${roomName}-${groupName}`;
          ids.add(groupId);

          if (!rooms[roomName].groups[groupName]) {
            rooms[roomName].groups[groupName] = [];
          }
          rooms[roomName].groups[groupName].push(itemWithIndex);
        } else {
          rooms[roomName].ungrouped.push(itemWithIndex);
        }

        // Module and component IDs
        const moduleId = `module-${item.order_item_id}-${index}`;
        ids.add(moduleId);

        if (item.base_types) {
          item.base_types.forEach((_, baseIdx) => {
            ids.add(`component-${item.order_item_id}-${index}-${baseIdx}`);
          });
        }
      } else {
        ungrouped.push(itemWithIndex);
        const moduleId = `module-${item.order_item_id}-${index}`;
        ids.add(moduleId);

        if (item.base_types) {
          item.base_types.forEach((_, baseIdx) => {
            ids.add(`component-${item.order_item_id}-${index}-${baseIdx}`);
          });
        }
      }
    });

    return {
      roomsData: rooms,
      ungroupedItems: ungrouped,
      allAccordionIds: ids,
    };
  }, [log?.order_items]);

  // Reset expanded state when modal opens
  useEffect(() => {
    if (isOpen) {
      setExpandedIds(new Set());
      setAllExpanded(false);
    }
  }, [isOpen]);

  const toggleAccordion = (id) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleExpandAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(allAccordionIds));
    }
    setAllExpanded(!allExpanded);
  };

  const isExpanded = (id) => expandedIds.has(id);

  // Calculate totals
  const getTotal = (items) =>
    items.reduce((sum, item) => sum + (item.price_difference || 0), 0);
  const getRoomTotal = (roomData) => {
    const groupsTotal = Object.values(roomData.groups).reduce(
      (sum, items) => sum + getTotal(items),
      0
    );
    return groupsTotal + getTotal(roomData.ungrouped);
  };

  const formatPrice = (price) => {
    const isPositive = price >= 0;
    return {
      display: `${isPositive ? "+" : ""}${formatCurrency(price)}`,
      isPositive,
    };
  };

  if (!log) return null;

  const isInitialSnapshot = log.initial_snapshot === true;
  const priceDiff = parseFloat(log.price_difference) || 0;
  const priceInfo = formatPrice(priceDiff);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle} className="border-bottom">
        <div className="d-flex align-items-center gap-3">
          <span>Price Change Details</span>
          <span className="text-muted small">
            {formatRelativeTime(log.created_at)}
          </span>
        </div>
      </ModalHeader>
      <ModalBody>
        {/* Summary Header */}
        <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
          <div>
            <div className="small text-muted">Total Change</div>
            <div
              className={`fw-bold fs-4 ${priceInfo.isPositive ? "text-success" : "text-danger"}`}
            >
              {priceInfo.display}
            </div>
          </div>
          <div className="text-end">
            <div className="small text-muted">New Total</div>
            <div className="fw-bold fs-5">
              {formatCurrency(log.new_total_price)}
            </div>
          </div>
        </div>

        {isInitialSnapshot ? (
          <div className="text-muted text-center py-4">
            Initial price snapshot - no changes to display
          </div>
        ) : (log.order_items || []).length === 0 ? (
          <div className="text-muted text-center py-4">
            No item changes in this log
          </div>
        ) : (
          <>
            {/* Expand/Collapse All Toggle */}
            <div className="d-flex justify-content-end mb-3">
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={toggleExpandAll}
              >
                <i
                  className={`fa-solid fa-${allExpanded ? "compress-alt" : "expand-alt"} me-2`}
                ></i>
                {allExpanded ? "Collapse All" : "Expand All"}
              </button>
            </div>

            {/* Rooms */}
            {Object.entries(roomsData).map(([roomName, roomData]) => {
              const roomTotal = getRoomTotal(roomData);
              const roomPrice = formatPrice(roomTotal);
              const roomId = `room-${roomName}`;
              const roomItemCount =
                Object.values(roomData.groups).flat().length +
                roomData.ungrouped.length;

              return (
                <AccordionItem
                  key={roomId}
                  title={`${roomName} (${roomItemCount} ${roomItemCount === 1 ? "module" : "modules"})`}
                  priceDisplay={roomPrice.display}
                  isPositive={roomPrice.isPositive}
                  isOpen={isExpanded(roomId)}
                  onToggle={() => toggleAccordion(roomId)}
                  level={0}
                >
                  {/* Sections/Groups */}
                  {Object.entries(roomData.groups).map(([groupName, items]) => {
                    const groupTotal = getTotal(items);
                    const groupPrice = formatPrice(groupTotal);
                    const groupId = `group-${roomName}-${groupName}`;

                    return (
                      <AccordionItem
                        key={groupId}
                        title={groupName}
                        priceDisplay={groupPrice.display}
                        isPositive={groupPrice.isPositive}
                        isOpen={isExpanded(groupId)}
                        onToggle={() => toggleAccordion(groupId)}
                        level={1}
                      >
                        {/* Modules */}
                        {items.map((item) => {
                          const itemPrice = formatPrice(
                            item.price_difference || 0
                          );
                          const moduleId = `module-${item.order_item_id}-${item.originalIndex}`;
                          const isAdded =
                            item.old_price === null ||
                            item.old_price === undefined;
                          const isRemoved =
                            item.new_price === null ||
                            item.new_price === undefined;
                          const badge = isAdded
                            ? { type: "added", text: "added" }
                            : isRemoved
                              ? { type: "removed", text: "removed" }
                              : null;
                          const hasComponents =
                            item.base_types && item.base_types.length > 0;

                          return (
                            <AccordionItem
                              key={moduleId}
                              title={item.order_item_name}
                              badge={badge}
                              priceDisplay={itemPrice.display}
                              isPositive={itemPrice.isPositive}
                              isOpen={isExpanded(moduleId)}
                              onToggle={() => toggleAccordion(moduleId)}
                              level={2}
                            >
                              {hasComponents &&
                                item.base_types.map((baseType, baseIdx) => {
                                  const basePrice = formatPrice(
                                    baseType.price_difference || 0
                                  );
                                  const componentId = `component-${item.order_item_id}-${item.originalIndex}-${baseIdx}`;
                                  const hasUpgrades =
                                    baseType.upgrades &&
                                    baseType.upgrades.length > 0;

                                  return (
                                    <AccordionItem
                                      key={componentId}
                                      title={baseType.name}
                                      priceDisplay={basePrice.display}
                                      isPositive={basePrice.isPositive}
                                      isOpen={isExpanded(componentId)}
                                      onToggle={() =>
                                        toggleAccordion(componentId)
                                      }
                                      level={3}
                                    >
                                      {hasUpgrades && (
                                        <div className="ms-5 ps-3">
                                          {baseType.upgrades.map(
                                            (upgrade, upgradeIdx) => {
                                              const upgradePrice = formatPrice(
                                                upgrade.price_difference || 0
                                              );
                                              return (
                                                <div
                                                  key={upgradeIdx}
                                                  className="d-flex justify-content-between align-items-center mb-1 py-1"
                                                >
                                                  <div className="small">
                                                    <span className="badge bg-secondary me-2">
                                                      {upgrade.addon_name}
                                                    </span>
                                                    <span className="text-muted">
                                                      {upgrade.old_value ||
                                                        "N/A"}{" "}
                                                      →{" "}
                                                      {upgrade.new_value ||
                                                        "N/A"}
                                                    </span>
                                                  </div>
                                                  <span
                                                    className={`small fw-semibold ${upgradePrice.isPositive ? "text-success" : "text-danger"}`}
                                                  >
                                                    {upgradePrice.display}
                                                  </span>
                                                </div>
                                              );
                                            }
                                          )}
                                        </div>
                                      )}
                                    </AccordionItem>
                                  );
                                })}
                              {/* Size changes */}
                              {item.size_changes &&
                                item.size_changes.length > 0 && (
                                  <div className="ms-5 ps-3">
                                    {item.size_changes.map(
                                      (sizeChange, sizeIdx) => {
                                        const sizePrice = formatPrice(
                                          sizeChange.price_difference || 0
                                        );
                                        const componentName =
                                          sizeChange.component_name ||
                                          sizeChange.addon_name ||
                                          "Component";
                                        const unit =
                                          sizeChange.unit || "sq.ft.";
                                        return (
                                          <div
                                            key={sizeIdx}
                                            className="d-flex justify-content-between align-items-center mb-1 py-1"
                                          >
                                            <div className="small text-muted">
                                              {componentName}:{" "}
                                              {sizeChange.old_size} {unit} →{" "}
                                              {sizeChange.new_size} {unit}
                                            </div>
                                            <span
                                              className={`small fw-semibold ${sizePrice.isPositive ? "text-success" : "text-danger"}`}
                                            >
                                              {sizePrice.display}
                                            </span>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                )}
                            </AccordionItem>
                          );
                        })}
                      </AccordionItem>
                    );
                  })}

                  {/* Ungrouped items within room */}
                  {roomData.ungrouped.map((item) => {
                    const itemPrice = formatPrice(item.price_difference || 0);
                    const moduleId = `module-${item.order_item_id}-${item.originalIndex}`;
                    const isAdded =
                      item.old_price === null || item.old_price === undefined;
                    const isRemoved =
                      item.new_price === null || item.new_price === undefined;
                    const badge = isAdded
                      ? { type: "added", text: "added" }
                      : isRemoved
                        ? { type: "removed", text: "removed" }
                        : null;
                    const hasComponents =
                      item.base_types && item.base_types.length > 0;

                    return (
                      <AccordionItem
                        key={moduleId}
                        title={item.order_item_name}
                        badge={badge}
                        priceDisplay={itemPrice.display}
                        isPositive={itemPrice.isPositive}
                        isOpen={isExpanded(moduleId)}
                        onToggle={() => toggleAccordion(moduleId)}
                        level={1}
                      >
                        {hasComponents &&
                          item.base_types.map((baseType, baseIdx) => {
                            const basePrice = formatPrice(
                              baseType.price_difference || 0
                            );
                            const componentId = `component-${item.order_item_id}-${item.originalIndex}-${baseIdx}`;
                            const hasUpgrades =
                              baseType.upgrades && baseType.upgrades.length > 0;

                            return (
                              <AccordionItem
                                key={componentId}
                                title={baseType.name}
                                priceDisplay={basePrice.display}
                                isPositive={basePrice.isPositive}
                                isOpen={isExpanded(componentId)}
                                onToggle={() => toggleAccordion(componentId)}
                                level={2}
                              >
                                {hasUpgrades && (
                                  <div className="ms-4 ps-3">
                                    {baseType.upgrades.map(
                                      (upgrade, upgradeIdx) => {
                                        const upgradePrice = formatPrice(
                                          upgrade.price_difference || 0
                                        );
                                        return (
                                          <div
                                            key={upgradeIdx}
                                            className="d-flex justify-content-between align-items-center mb-1 py-1"
                                          >
                                            <div className="small">
                                              <span className="badge bg-secondary me-2">
                                                {upgrade.addon_name}
                                              </span>
                                              <span className="text-muted">
                                                {upgrade.old_value || "N/A"} →{" "}
                                                {upgrade.new_value || "N/A"}
                                              </span>
                                            </div>
                                            <span
                                              className={`small fw-semibold ${upgradePrice.isPositive ? "text-success" : "text-danger"}`}
                                            >
                                              {upgradePrice.display}
                                            </span>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                )}
                              </AccordionItem>
                            );
                          })}
                      </AccordionItem>
                    );
                  })}
                </AccordionItem>
              );
            })}

            {/* Ungrouped Items (no room) */}
            {ungroupedItems.map((item) => {
              const itemPrice = formatPrice(item.price_difference || 0);
              const moduleId = `module-${item.order_item_id}-${item.originalIndex}`;
              const isAdded =
                item.old_price === null || item.old_price === undefined;
              const isRemoved =
                item.new_price === null || item.new_price === undefined;
              const badge = isAdded
                ? { type: "added", text: "added" }
                : isRemoved
                  ? { type: "removed", text: "removed" }
                  : null;
              const hasComponents =
                item.base_types && item.base_types.length > 0;

              return (
                <AccordionItem
                  key={moduleId}
                  title={item.order_item_name}
                  badge={badge}
                  priceDisplay={itemPrice.display}
                  isPositive={itemPrice.isPositive}
                  isOpen={isExpanded(moduleId)}
                  onToggle={() => toggleAccordion(moduleId)}
                  level={0}
                >
                  {hasComponents &&
                    item.base_types.map((baseType, baseIdx) => {
                      const basePrice = formatPrice(
                        baseType.price_difference || 0
                      );
                      const componentId = `component-${item.order_item_id}-${item.originalIndex}-${baseIdx}`;
                      const hasUpgrades =
                        baseType.upgrades && baseType.upgrades.length > 0;

                      return (
                        <AccordionItem
                          key={componentId}
                          title={baseType.name}
                          priceDisplay={basePrice.display}
                          isPositive={basePrice.isPositive}
                          isOpen={isExpanded(componentId)}
                          onToggle={() => toggleAccordion(componentId)}
                          level={1}
                        >
                          {hasUpgrades && (
                            <div className="ms-3 ps-3">
                              {baseType.upgrades.map((upgrade, upgradeIdx) => {
                                const upgradePrice = formatPrice(
                                  upgrade.price_difference || 0
                                );
                                return (
                                  <div
                                    key={upgradeIdx}
                                    className="d-flex justify-content-between align-items-center mb-1 py-1"
                                  >
                                    <div className="small">
                                      <span className="badge bg-secondary me-2">
                                        {upgrade.addon_name}
                                      </span>
                                      <span className="text-muted">
                                        {upgrade.old_value || "N/A"} →{" "}
                                        {upgrade.new_value || "N/A"}
                                      </span>
                                    </div>
                                    <span
                                      className={`small fw-semibold ${upgradePrice.isPositive ? "text-success" : "text-danger"}`}
                                    >
                                      {upgradePrice.display}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </AccordionItem>
                      );
                    })}
                </AccordionItem>
              );
            })}
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

const PriceChangeLog = ({ projectId, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const prevProjectIdRef = useRef(null);

  useEffect(() => {
    if (!projectId) return;

    const fetchLogs = async () => {
      setIsLoading(true);
      try {
        const response = await getPriceChangeLogs({ projectId });
        const data = response.data || response;
        setLogs(data.price_change_logs || []);
      } catch (error) {
        console.error("Error fetching price change logs:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Fetch logs when projectId changes
    if (prevProjectIdRef.current !== projectId) {
      prevProjectIdRef.current = projectId;
      fetchLogs();
    }
  }, [projectId]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "–";
    const numValue = typeof value === "number" ? value : parseFloat(value);
    if (isNaN(numValue)) return "–";
    // Round to whole numbers - no decimal points
    return `₹${Math.round(numValue).toLocaleString("en-IN")}`;
  };

  const openLogDetail = (log) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLog(null);
  };

  return (
    <div className="h-100 d-flex flex-column">
      <div className="flex-grow-1 overflow-auto">
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner />
          </div>
        ) : logs.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center h-100 text-muted">
            <p className="m-0">No price change logs found</p>
          </div>
        ) : (
          <div className="m-2 border-bottom">
            {logs.map((log) => {
              const isInitialSnapshot = log.initial_snapshot === true;
              const priceDiff = parseFloat(log.price_difference) || 0;
              const isPositive = priceDiff >= 0;

              return (
                <div key={log.id} className="mb-0 bg-white">
                  {/* Card Row - Click to open modal */}
                  <div
                    className="border border-bottom-0 p-3 hover-shadow"
                    onClick={() => openLogDetail(log)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="text-muted small">
                        {formatRelativeTime(log.created_at)}
                        {isInitialSnapshot && (
                          <span className="badge bg-secondary ms-2">
                            Initial Snapshot
                          </span>
                        )}
                      </div>
                      {!isInitialSnapshot && (
                        <div className="d-flex align-items-center gap-2">
                          <div className="fw-bold">
                            {formatCurrency(log.new_total_price)}
                          </div>
                          <div>
                            {isPositive ? (
                              <i className="fa-solid fa-arrow-up text-success"></i>
                            ) : (
                              <i className="fa-solid fa-arrow-down text-danger"></i>
                            )}
                          </div>
                          <div
                            className={`fw-bold ${
                              isPositive ? "text-success" : "text-danger"
                            }`}
                          >
                            {isPositive ? "+" : ""}
                            {formatCurrency(priceDiff)}
                          </div>
                          <i className="fa-solid fa-chevron-right text-muted ms-2"></i>
                        </div>
                      )}
                      {isInitialSnapshot && log.new_total_price && (
                        <div className="d-flex align-items-center gap-2">
                          <div className="fw-bold">
                            {formatCurrency(log.new_total_price)}
                          </div>
                          <i className="fa-solid fa-chevron-right text-muted ms-2"></i>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <PriceLogDetailModal
        isOpen={isModalOpen}
        toggle={closeModal}
        log={selectedLog}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default PriceChangeLog;
